import { useCallback, useEffect } from 'react';
import { batch, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_ANALYSIS } from '../../utils/apollo/analysis';
import {
  AnalysisStatus,
  IAnalysisQuery,
  IAnalysisQueryVariables,
} from '../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../utils/types';
import { updateAnalysisVisitStatus } from '../../redux/slices/analysesVisits';
import { completeExecutingAnalysis } from '../../redux/slices/executingAnalyses';
import { getQueryTypePagePath } from '../../utils';
import { populateAnalysisThunk } from '../../redux/slices/analyses';
import { IAnalysisResponse } from '../../utils/apollo/responseTypes';
import toastMessage from '../../utils/toastMessage';

interface IProps {
  analysisId: string;
}

const POLL_INTERVAL = 3000;

const POLLING_STATUSES = [AnalysisStatus.Pending, AnalysisStatus.Running];

const checkCurrentStatus = (status: AnalysisStatus): boolean =>
  POLLING_STATUSES.includes(status);

export default ({ analysisId }: IProps) => {
  const dispatch = useDispatch();

  const location = useLocation();

  const { data, startPolling, stopPolling } = useQuery<
    IAnalysisQuery,
    IAnalysisQueryVariables
  >(GET_ANALYSIS, {
    variables: {
      analysisId,
    },
  });

  // A callback that would be fired when execution is complete
  const handleCompletePolling = useCallback(
    (analysis: IAnalysisResponse) => {
      assertNotNil(analysis);

      // Update store
      batch(() => {
        // backup action to set status on history.
        dispatch(
          updateAnalysisVisitStatus({
            id: analysis.id,
            status: analysis.status,
          }),
        );
        dispatch(populateAnalysisThunk({ analysis }));
        dispatch(
          completeExecutingAnalysis({
            analysisId: analysis.id,
          }),
        );
      });

      const targetPathName = getQueryTypePagePath(
        analysis.queryType,
        analysis.id,
      );

      // If current location is not the same as executed analysis
      // we will provide a toast with onClick handler that will redirect
      // user to completed analysis
      if (!targetPathName.includes(location.pathname)) {
        if (analysis.status === AnalysisStatus.Failed) {
          toastMessage({
            message: `Execution of ${analysis.display} report Failed.`,
            redirectPath: targetPathName,
            type: 'error',
          });
        } else {
          toastMessage({
            message: `Report completed: ${analysis.display}.`,
            redirectPath: targetPathName,
            type: 'action',
          });
        }
      }
    },
    [dispatch, location],
  );

  // Handling start polling
  useEffect(() => {
    const status = data?.analysis?.status;

    if (status && checkCurrentStatus(status)) {
      startPolling(POLL_INTERVAL);
    }
  }, [data, startPolling]);

  // Handling stop polling
  useEffect(() => {
    const status = data?.analysis?.status;

    if (status && !checkCurrentStatus(status)) {
      stopPolling();

      if (data?.analysis) {
        handleCompletePolling(data?.analysis);
      }
    }
  }, [data, handleCompletePolling, stopPolling]);

  return null;
};
